import { useEffect } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Appearance: Light (the default) or Dark, chosen with the light / dark toggle
 * (Kazzy, 30 Oct 2026). Applied as `data-theme` on <html>, the attribute
 * web-v2 uses. Warm (light with oat neutrals, `data-palette="warm"`) is hidden
 * from every picker but still applies when chosen, for the Warm button Kazzy
 * means to add; Colourful is gone, and a choice stored as Colourful reads as
 * Light. `system` (follow the OS) is still honoured for a choice stored before
 * the looks existed. index.html applies the stored choice before first paint;
 * this store keeps it in step.
 */
export type Appearance = 'system' | 'light' | 'colourful' | 'warm' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

/** The looks a picker offers (Settings' cards). */
export const APPEARANCES = ['light', 'dark'] as const satisfies readonly Appearance[];
export const APPEARANCE_NAMES: Record<(typeof APPEARANCES)[number], string> = {
  light: 'Light',
  dark: 'Dark',
};

interface ThemeState {
  appearance: Appearance;
  setAppearance: (appearance: Appearance) => void;
}

const safeStorage = createJSONStorage(() => {
  try {
    return window.localStorage;
  } catch {
    return undefined as unknown as Storage;
  }
});

const systemDark = () => typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

export function resolveTheme(appearance: Appearance): ResolvedTheme {
  if (appearance === 'dark') return 'dark';
  if (appearance === 'system') return systemDark() ? 'dark' : 'light';
  return 'light';
}

export const THEME_CHANGE_EVENT = 'dw-theme-change';

function apply(appearance: Appearance) {
  const root = document.documentElement;
  const resolved = resolveTheme(appearance);
  root.dataset.theme = resolved;
  if (appearance === 'warm') root.dataset.palette = appearance;
  else delete root.dataset.palette;
  try {
    // The pre-paint script in index.html reads this key.
    window.localStorage.setItem('dw-theme', appearance);
  } catch {
    /* storage blocked: the choice lasts for this tab */
  }
  root.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: { resolved, appearance } }));
}

/** A change of look cross-fades the whole window (380ms, app.css), unless motion is reduced or the browser can't. */
function withViewTransition(change: () => void) {
  const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || typeof document === 'undefined' || typeof document.startViewTransition !== 'function') {
    change();
    return;
  }
  document.startViewTransition(change);
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      appearance: 'light',
      setAppearance: (appearance) =>
        withViewTransition(() => {
          set({ appearance });
          apply(appearance);
        }),
    }),
    { name: 'dw-theme-store', storage: safeStorage },
  ),
);

/** Mount once (root route): keeps <html> in step with the store and the OS. */
export function useThemeSync() {
  const appearance = useThemeStore((s) => s.appearance);
  useEffect(() => {
    apply(appearance);
    if (appearance !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => apply('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [appearance]);
}

export function readResolvedTheme(): ResolvedTheme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}
